// Calculates measures via direct SoQL calls.
// Exports from this function are intended to be the module's public API.
// Internal helpers live in the sibling file `helpers.js`.
// We export the helpers to facilitate testing.

import _ from 'lodash';
import moment from 'moment';
import { getDateRangeFilters } from 'common/components/CommonFilterHelpers';
import { assertIsOneOfTypes } from 'common/assertions/assertIsOneOfTypes';
import { UID_REGEX } from 'common/http/constants';

import * as ReportingPeriods from '../lib/reportingPeriods';
import { CalculationTypes, PeriodTypes } from '../lib/constants';

import { applyAdditionalFiltersToMeasure, getMeasureConfigurationErrors, getLastDataTime } from './helpers';

import { calculateAverageMeasure } from './average';
import { calculateBatchedMeasures, shouldUseBatchOptimization } from './batch';
import { calculateCountMeasure } from './count';
import { calculateRateMeasure } from './rate';
import { calculateRecentValueMeasure } from './recent_value';
import { calculateSumMeasure } from './sum';
import {
  type ComputedMeasureSeries,
  type Measure,
  type MeasureErrors,
  type MetricSeriesResult,
} from '../types';
import type DateRange from '../lib/dateRange';

function markFetchStart(markId: string) {
  if (markId && window.performance.mark) {
    window.performance.mark(`ReportingPeriodsCalc:${markId}:Start`);
  }
}

function markFetchFinish(markId: string) {
  if (window.performance.mark) {
    window.performance.mark(`ReportingPeriodsCalc:${markId}:Finish`);
    window.performance.measure(
      `ReportingPeriodsCalc:${markId}`,
      `ReportingPeriodsCalc:${markId}:Start`,
      `ReportingPeriodsCalc:${markId}:Finish`,
    );

    performance.clearMarks(`ReportingPeriodsCalc:${markId}:Finish`);
    performance.clearMarks(`ReportingPeriodsCalc:${markId}:Start`);
    performance.clearMeasures(`ReportingPeriodsCalc:${markId}:Start`);
  }
}

// These helpers are used internally by other helpers
// as well as parts of the UI (outside this module). We
// re-export them here to maintain a clearer distinction
// between public API (this file) and private API.
export { measureArgumentNeedsNumericColumn, isColumnUsableWithMeasureArgument } from './helpers';

export const calculateMeasure = async (
  measure: Measure | null,
  dateRange: DateRange | null,
): Promise<Omit<MetricSeriesResult, 'date'>> => {
  assertIsOneOfTypes(measure, 'object');
  // Our error detection logic needs to be very robust and be able to provide sensible guidance to the user
  // in common cases. The easiest way we came up with is to have an errors hash that we poke error states
  // into as we go along.
  const errors: MeasureErrors = {};

  const calculationType = measure?.metricConfig?.type;
  const dateColumn = measure?.metricConfig?.dateColumn;
  const reportingPeriod = measure?.metricConfig?.reportingPeriod;
  // Did the user even specify a reporting period?
  errors.noReportingPeriodConfigured = !ReportingPeriods.isFullyConfigured(reportingPeriod);
  // ... and if they did, does it give us a valid date range for today's value?
  errors.noReportingPeriodAvailable = !dateRange;

  errors.dataSourceNotConfigured = !measure?.dataSourceLensUid || !UID_REGEX.test(measure?.dataSourceLensUid);

  measure = applyAdditionalFiltersToMeasure(measure);

  // A blank dateRange can happen if:
  //   * The start date is in the future, or
  //   * The period type is "closed" and we haven't closed any reporting periods (i.e.,
  //     it's 1/1/2018, the period length is 1 year, and the first period started on 6/01/2017).
  // Account for measures with cumulative math enabled
  dateRange = dateRange ? dateRange.forCalculation(measure) : null;
  const dateRangeWhereClause = dateRange && dateColumn ? dateRange.asSoQL(dateColumn) : null;

  switch (calculationType) {
    case CalculationTypes.AVERAGE:
      return calculateAverageMeasure(errors, measure, dateRangeWhereClause);
    case CalculationTypes.COUNT:
      return calculateCountMeasure(errors, measure, dateRangeWhereClause);
    case CalculationTypes.SUM:
      return calculateSumMeasure(errors, measure, dateRangeWhereClause);
    case CalculationTypes.RECENT:
      return calculateRecentValueMeasure(errors, measure, dateRangeWhereClause);
    case CalculationTypes.RATE:
      return calculateRateMeasure(errors, measure, dateRangeWhereClause);
    case undefined:
      errors.calculationNotConfigured = true;
      return { errors };
    default:
      throw new Error(`Unknown calculation type: ${calculationType}`);
  }
};

/**
 * The getMetricSeries function returns the computed data for each reporting period given the
 * measure that is passed in to the function. This function is used for both the metric card
 * and the metric viz. Data that gets fetched back can be filtered and transformed into the
 * format that the metric viz expects.
 *
 * Returns an array of objects:
 * {
 *   date: String of the starting date of the reporting period
 *   result: {
 *     value: BigNumber Object
 *     numerator: BigNumber Object (Rate measures only)
 *     denominator: BigNumber Object (Rate measures only)
 *   },
 *   errors: {
 *    dividingByZero: Boolean (Rate measures only). Indicates the denominator is zero.
 *    dataSourceNotConfigured: Boolean. If set to true, indicates that the data source is not configured.
 *    noRecentValue: Boolean (Recent Value measures only). If set to true, the selected reference date column
 *     does not contain any non-null values.
 *    noReportingPeriodAvailable: Boolean. If set to true, indicates that no reporting period is usable
 *     (this can happen if the start date is in the future, or we're using closed reporting periods and
 *     no period has closed yet).
 *    noReportingPeriodConfigured: Boolean. If set to true, indicates that no reporting period was set
 *     by the user.
 *    calculationNotConfigured: Boolean. Indicates an insufficiently-specified calculation.
 *   }
 * }
 */
export const getMetricSeries = async (
  measure: Measure | null,
  { lastPeriodOnly = false } = {},
): Promise<{ errors?: MeasureErrors; series: MetricSeriesResult[] }> => {
  // When querying for data, also grab the last reporting data datetime. We will need
  // it for various edge cases later on.
  let firstDataMoment;
  let lastDataMoment = await getLastDataTime(measure);
  const errors = getMeasureConfigurationErrors(measure, lastDataMoment);

  if (Object.keys(errors).length > 0) {
    // There's not enough information to calculate this measure.
    return { errors, series: [] };
  }

  /**
   * In order for GFB date filters to work properly for measure, we need to override the
   * lastDataMoment if the date range filters from the GFB are before the lastDataMoment
   */
  const additionalFilters = measure?.metricConfig?.additionalFilters;
  const reportingType = measure?.metricConfig?.reportingPeriod?.type;
  const reportingPeriodConfig = measure?.metricConfig?.reportingPeriod;

  /**
   * This is currently gated with the Last Reporting Type but it doesn't needs to be.
   * This was done to limit the scope of the fix. We could open this to other reporting
   * types but we would need to update logic to display the proper date ranges on
   * the measure card
   */
  if (additionalFilters && reportingType === PeriodTypes.LAST_REPORTED) {
    const dateRangeFilters = getDateRangeFilters(additionalFilters);
    // This assumes that there will only be one GFB time range filter on a measure
    const lastFilteredDate = dateRangeFilters[0]?.arguments?.end;
    const firstFilteredDate = dateRangeFilters[0]?.arguments?.start;
    const configurationStartDate = reportingPeriodConfig
      ? ReportingPeriods.getStartDate(reportingPeriodConfig)
      : undefined;
    if (
      firstFilteredDate &&
      configurationStartDate &&
      moment(firstFilteredDate).isAfter(configurationStartDate)
    ) {
      firstDataMoment = moment(firstFilteredDate);
    }
    if (lastFilteredDate && moment(lastFilteredDate).isBefore(lastDataMoment)) {
      lastDataMoment = moment(lastFilteredDate);
    }
  }

  let reportingPeriods = ReportingPeriods.getSeries(reportingPeriodConfig!, lastDataMoment, firstDataMoment);

  if (lastPeriodOnly) {
    reportingPeriods = _.takeRight(reportingPeriods);
  }

  const markId = _.uniqueId();
  markFetchStart(markId);

  let measureResultsPerReportingPeriod: Omit<MetricSeriesResult, 'date'>[];

  // Helper function to calculate measures using individual queries
  const calculateIndividualMeasures = async () => {
    const calculationPromises = reportingPeriods.map((dateRange) => calculateMeasure(measure, dateRange));
    return Promise.all(calculationPromises);
  };

  // Determine if batch optimization should be used and calculate accordingly
  const shouldUseBatch = shouldUseBatchOptimization(measure, reportingPeriods, lastPeriodOnly);

  if (shouldUseBatch && measure) {
    try {
      // Use batch calculation for supported measure types
      measureResultsPerReportingPeriod = await calculateBatchedMeasures(measure, reportingPeriods);
    } catch (error) {
      // Fallback to original method if batch fails
      console.warn('Batch calculation failed, falling back to individual queries:', error);
      measureResultsPerReportingPeriod = await calculateIndividualMeasures();
    }
  } else {
    // Use original individual query approach
    measureResultsPerReportingPeriod = await calculateIndividualMeasures();
  }

  markFetchFinish(markId);

  return {
    series: measureResultsPerReportingPeriod.map((measureResult, i) => {
      const startDate = reportingPeriods[i].start.format('YYYY-MM-DDTHH:mm:ss.SSS');
      // Must always return null for a value that cant be calculated since charts dont know how to deal with
      // other nil-y values like undefined
      const result = measureResult.result?.value || null;
      const errorsForResult = measureResult?.errors || null;

      // This should be refactored at some point
      const denominator = measureResult.result?.denominator || null;
      const numerator = measureResult.result?.numerator || null;

      return {
        date: startDate,
        result: {
          value: result,
          numerator,
          denominator,
        },
        errors: errorsForResult,
      };
    }),
  };
};

/**
 * Filters out null value and any bigNumber object that does not have a finite value
 * @returns MetricSeriesResult[]
 */
export const getNonNullResults = (metricSeriesResult: MetricSeriesResult[]): MetricSeriesResult[] => {
  const nonNullData = _.filter(metricSeriesResult, (data: MetricSeriesResult) => {
    return !!data.result?.value?.isFinite();
  });

  return nonNullData;
};

/**
 * Transforms the results from getMetricSeries into what the metric viz is expecting.
 */
export const toVizData = (metricSeriesResult: MetricSeriesResult[]): ComputedMeasureSeries =>
  metricSeriesResult.map(({ date, result }) => [date, result?.value]);
